import { Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { StatusBadge } from '@/components/StatusBadge';
import { ColumnFilter, MonthRangeFilter, type MonthRangeFilterProps } from '@/components/ColumnFilter';
import type { DataColumn } from '@/components/DataTable';
import { CardNo, LoanLink, MemberLink, PolicyLink } from '@/components/links/RecordLinks';
import type { Loan } from '@/types';
import { formatCurrency, formatPaymentDate, paymentTypeLabel } from '@/lib/payments';
import { displayMemberNo } from '@/lib/utils';
import { ICON_BUTTON_CLASS } from '@/lib/styles';

export type LoanColumn = DataColumn<Loan>;

export interface LoanRowActions {
  onOpenLoan: (loan: Loan) => void;
  /** This Month header choice: '' = none, otherwise 'paid', 'unpaid' or 'not_due'. UI only for now. */
  thisMonthFilter: { value: string; onChange: (value: string) => void };
  /** Given On month range picked in the header. UI only for now. */
  givenOnFilter: MonthRangeFilterProps;
}

const THIS_MONTH_TEXT = {
  paid: { label: 'Paid', className: 'text-teal-700' },
  unpaid: { label: 'Due', className: 'text-amber-700' },
  not_due: { label: 'Not Due', className: 'text-gray-400' },
} as const;

function RepaymentCell({ loan }: { loan: Loan }) {
  const percent = loan.amount > 0 ? Math.round((loan.principalPaid / loan.amount) * 100) : 0;
  return (
    <div className="min-w-[140px] space-y-1.5">
      <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full ${loan.status === 'closed' ? 'bg-gray-400' : 'bg-[#4369a0]'}`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="whitespace-nowrap text-xs tabular-nums text-gray-500">
        {formatCurrency(loan.principalPaid)} / {formatCurrency(loan.amount)} ({percent}%)
      </div>
    </div>
  );
}

function ThisMonthText({ loan }: { loan: Loan }) {
  if (loan.status === 'closed') return <span className="text-gray-400">Closed</span>;
  const item = THIS_MONTH_TEXT[loan.thisMonth ?? 'not_due'];
  return <span className={item.className}>{item.label}</span>;
}

function LastPaymentText({ loan }: { loan: Loan }) {
  if (!loan.lastPayment) return <span className="text-gray-400">No Payment Yet</span>;
  return (
    <span className="whitespace-nowrap text-gray-700">
      {formatPaymentDate(loan.lastPayment.date)} <span className="text-gray-400">· {paymentTypeLabel(loan.lastPayment.type)}</span>
    </span>
  );
}

export const createColumns = ({ onOpenLoan, thisMonthFilter, givenOnFilter }: LoanRowActions): LoanColumn[] => [
  {
    id: 'id',
    header: 'ID',
    className: 'w-[64px]',
    render: (loan) => <span className="flex w-[13px] justify-center whitespace-nowrap tabular-nums text-gray-500 font-medium">{displayMemberNo({ id: loan.memberId, memberNo: loan.memberNo })}</span>,
  },
  { id: 'cardNo', header: 'Card No', render: (loan) => <CardNo value={loan.cardNo} /> },
  {
    id: 'member',
    header: 'Member',
    render: (loan) => (
      <div>
        <MemberLink memberId={loan.memberId} memberNo={loan.memberNo} name={loan.memberName} className="block font-medium text-gray-900" />
        <span className="text-xs text-gray-500">{loan.memberPhone}</span>
      </div>
    ),
  },
  {
    id: 'loanNo',
    header: 'Loan No',
    render: (loan) => <LoanLink member={{ id: loan.memberId, name: loan.memberName, memberNo: loan.memberNo }} loanNo={loan.loanNo} className="font-medium text-gray-900" />,
  },
  { id: 'policy', header: 'Policy', render: (loan) => <PolicyLink member={{ id: loan.memberId, name: loan.memberName, memberNo: loan.memberNo }} policyNo={loan.policyNo} /> },
  {
    id: 'givenOn',
    header: <MonthRangeFilter label="Given On" {...givenOnFilter} />,
    render: (loan) => formatPaymentDate(loan.givenOn),
  },
  {
    id: 'amount',
    header: 'Loan Amount',
    className: 'text-right',
    render: (loan) => <span className="tabular-nums">{formatCurrency(loan.amount)}</span>,
  },
  { id: 'repayment', header: 'Repayment', render: (loan) => <RepaymentCell loan={loan} /> },
  {
    id: 'outstanding',
    header: 'Outstanding',
    className: 'text-right',
    render: (loan) => <span className="font-medium tabular-nums">{formatCurrency(loan.outstanding)}</span>,
  },
  {
    id: 'thisMonth',
    header: (
      <ColumnFilter
        label="This Month"
        options={[
          { value: 'paid', label: 'Paid' },
          { value: 'unpaid', label: 'Due' },
          { value: 'not_due', label: 'Not Due' },
        ]}
        value={thisMonthFilter.value}
        onChange={thisMonthFilter.onChange}
      />
    ),
    render: (loan) => <ThisMonthText loan={loan} />,
  },
  { id: 'lastPayment', header: 'Last Payment', render: (loan) => <LastPaymentText loan={loan} /> },
  { id: 'status', header: 'Status', render: (loan) => <StatusBadge status={loan.status} /> },
  {
    id: 'actions',
    header: 'Actions',
    className: 'text-center',
    render: (loan) => (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" onClick={() => onOpenLoan(loan)} className={ICON_BUTTON_CLASS}>
            <Wallet className="h-3.5 w-3.5" />
            <span className="sr-only">Open Loan</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Open Loan</TooltipContent>
      </Tooltip>
    ),
  },
];
